import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQC } from './QCContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Check,
  CreditCard,
  Users,
  Zap,
  Crown,
  Building,
  Shield,
} from 'lucide-react';

const tierIcons = {
  free: Users,
  starter: Zap,
  professional: Crown,
  enterprise: Building,
};

export const QCSubscription = () => {
  const navigate = useNavigate();
  const { api, user } = useQC();
  const [tiers, setTiers] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tiersRes, subRes] = await Promise.all([
        api.get('/subscription/tiers'),
        api.get('/subscription/current'),
      ]);
      setTiers(tiersRes.data || []);
      setCurrentSub(subRes.data);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (tierId) => {
    if (tierId === 'free') {
      toast.info('You are already on the Free plan');
      return;
    }

    setUpgrading(tierId);
    try {
      const response = await api.post(`/subscription/checkout?tier_id=${tierId}`);
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start upgrade process');
    } finally {
      setUpgrading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="qc-subscription">
      <Button variant="ghost" onClick={() => navigate('/quickcanvass')} className="gap-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </Button>

      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">
          Subscription
        </h1>
        <p className="text-muted-foreground mt-2">
          Choose the plan that best fits your organization
        </p>
      </div>

      {/* Current Plan Status */}
      {currentSub && (
        <Card className="bg-primary/5 border-primary">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Current Plan: {currentSub.tier_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {currentSub.current_employees} / {currentSub.employees_limit === -1 ? 'Unlimited' : currentSub.employees_limit} employees
                  </p>
                </div>
              </div>
              <Badge variant={currentSub.status === 'active' ? 'default' : 'secondary'}>
                {currentSub.status}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pricing Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiers.map((tier) => {
          const Icon = tierIcons[tier.id] || Users;
          const isCurrent = currentSub?.tier_id === tier.id;
          const isPopular = tier.id === 'professional';

          return (
            <Card 
              key={tier.id} 
              className={`relative ${isCurrent ? 'border-2 border-primary ring-2 ring-primary/20' : ''} ${isPopular ? 'border-2 border-amber-500' : ''}`}
            >
              {isPopular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500">
                  Most Popular
                </Badge>
              )}
              {isCurrent && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                  Current Plan
                </Badge>
              )}
              
              <CardHeader className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Icon className="h-6 w-6" />
                </div>
                <CardTitle>{tier.name}</CardTitle>
                <CardDescription>
                  {tier.employees === -1 ? 'Unlimited employees' : `Up to ${tier.employees} employees`}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="text-center">
                <div className="mb-6">
                  <span className="text-4xl font-black">${tier.price}</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                
                <ul className="space-y-2 text-left">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter>
                <Button 
                  className="w-full" 
                  variant={isCurrent ? 'outline' : tier.id === 'free' ? 'outline' : 'default'}
                  disabled={isCurrent || upgrading === tier.id}
                  onClick={() => handleUpgrade(tier.id)}
                >
                  {upgrading === tier.id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : isCurrent ? (
                    'Current Plan'
                  ) : tier.id === 'free' ? (
                    'Free Plan'
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Upgrade
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* FAQ */}
      <Card>
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium">Can I cancel anytime?</h4>
            <p className="text-sm text-muted-foreground">Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.</p>
          </div>
          <div>
            <h4 className="font-medium">What payment methods do you accept?</h4>
            <p className="text-sm text-muted-foreground">We accept all major credit cards through our secure Stripe payment processor.</p>
          </div>
          <div>
            <h4 className="font-medium">What happens if I exceed my employee limit?</h4>
            <p className="text-sm text-muted-foreground">You'll receive a notification to upgrade. Existing employees will continue to work, but you won't be able to add new ones until you upgrade.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QCSubscription;
